import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'wouter';
import { Loader2, Search, X } from 'lucide-react';
import { getShopifyProductsPage, type ShopifyProduct } from '@workspace/api-client-react';
import { useCurrency } from '@/contexts/currency-context';
import { useLanguage } from '@/contexts/language-context';
import { getLocalizedProductContent } from '@/i18n/product-translations';

const PRODUCTS_PER_PAGE = 50;

function normalizeSearchText(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
}

function ProductResults({
  id,
  products,
  query,
  language,
  isLoading,
  hasError,
  onRetry,
  onSelect,
  t,
}: {
  id: string;
  products: ShopifyProduct[];
  query: string;
  language: 'en' | 'fr' | 'ar';
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  onSelect: () => void;
  t: (message: string) => string;
}) {
  if (!query.trim()) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 px-6 py-10 text-sm text-muted-foreground" role="status">
        <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
        {t('Searching products')}
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm text-muted-foreground">{t('Could not load products. Please try again.')}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 border-b border-primary pb-1 text-xs font-bold uppercase tracking-widest text-primary hover:text-foreground"
        >
          {t('Retry search')}
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <p className="px-6 py-8 text-center text-sm text-muted-foreground" role="status">
        {t('No matching products')}
      </p>
    );
  }

  return (
    <div>
      <p className="px-4 pb-2 pt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {t('Product results')}
      </p>
      <ul id={id} aria-label={t('Product results')} className="divide-y divide-border">
        {products.map((product) => {
          const localized = getLocalizedProductContent(product, language);
          return (
            <li key={product.id}>
              <Link
                href={`/product/${product.handle}`}
                onClick={onSelect}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
              >
                <span className="flex h-14 w-12 shrink-0 items-center justify-center overflow-hidden bg-muted/40">
                  {product.featuredImage?.url ? (
                    <img
                      src={product.featuredImage.url}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-serif text-base text-foreground">{localized.title}</span>
                  <span className="mt-1 block truncate text-xs text-muted-foreground">
                    {product.tags?.slice(0, 2).join(' · ') || localized.description}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function ProductSearch() {
  const { countryCode } = useCurrency();
  const { language, shopifyLanguageCode, t } = useLanguage();
  const [query, setQuery] = useState('');
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loadedCatalogKey, setLoadedCatalogKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const catalogKey = `${countryCode}:${shopifyLanguageCode}`;
  const hasQuery = Boolean(query.trim());

  useEffect(() => {
    if (!hasQuery || loadedCatalogKey === catalogKey) return;

    let active = true;
    const controller = new AbortController();
    setProducts([]);
    setHasError(false);
    setIsLoading(true);

    const loadCatalog = async () => {
      const loadedProducts: ShopifyProduct[] = [];
      const seenProductIds = new Set<string>();
      let after: string | undefined;
      let hasNextPage = true;

      while (hasNextPage) {
        const page = await getShopifyProductsPage(
          {
            countryCode,
            languageCode: shopifyLanguageCode,
            first: PRODUCTS_PER_PAGE,
            ...(after ? { after } : {}),
          },
          { signal: controller.signal },
        );

        for (const product of page.products) {
          if (seenProductIds.has(product.id)) continue;
          seenProductIds.add(product.id);
          loadedProducts.push(product);
        }

        after = page.endCursor ?? undefined;
        hasNextPage = page.hasNextPage && Boolean(after);
      }

      if (active) {
        setProducts(loadedProducts);
        setLoadedCatalogKey(catalogKey);
      }
    };

    void loadCatalog()
      .catch(() => {
        if (active && !controller.signal.aborted) setHasError(true);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [hasQuery, countryCode, shopifyLanguageCode, catalogKey, loadedCatalogKey, retryVersion]);

  const matchingProducts = useMemo(() => {
    const terms = normalizeSearchText(query).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];

    return products.filter((product) => {
      const localized = getLocalizedProductContent(product, language);
      const searchableText = normalizeSearchText([
        product.title,
        localized.title,
        product.description,
        localized.description,
        product.handle,
        ...(product.tags ?? []),
      ].join(' '));
      return terms.every((term) => searchableText.includes(term));
    });
  }, [products, query, language, t]);

  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !desktopSearchRef.current?.contains(target) &&
        !mobileSearchRef.current?.contains(target)
      ) {
        setIsResultsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsResultsOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const closeSearch = () => {
    setQuery('');
    setIsResultsOpen(false);
  };

  const clearSearch = () => {
    setQuery('');
    setIsResultsOpen(false);
  };
  const retrySearch = () => {
    setLoadedCatalogKey(null);
    setHasError(false);
    setRetryVersion((version) => version + 1);
  };

  const searchField = (inputId: string, resultsId: string, placeholder: string) => (
    <form role="search" onSubmit={(event) => event.preventDefault()}>
      <label htmlFor={inputId} className="sr-only">{t('Search products')}</label>
      <div className="flex h-12 items-center gap-3 border border-border bg-muted/10 px-4 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <Search className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsResultsOpen(true);
          }}
          onFocus={() => setIsResultsOpen(true)}
          placeholder={placeholder}
          aria-controls={isResultsOpen && hasQuery ? resultsId : undefined}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          autoComplete="off"
          data-testid={`${inputId}-input`}
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            aria-label={t('Clear search')}
            className="shrink-0 p-1 text-muted-foreground transition-colors hover:text-primary"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </form>
  );

  return (
    <>
      <div ref={desktopSearchRef} className="relative hidden lg:col-start-3 lg:row-start-1 lg:block">
        {searchField('desktop-product-search', 'desktop-product-search-results', t('Search products'))}
        {isResultsOpen && hasQuery && (
          <div
            id="desktop-product-search-results"
            className="absolute right-0 top-full z-50 mt-2 max-h-[min(65dvh,36rem)] w-[min(30rem,calc(100vw-2rem))] overflow-y-auto border border-border bg-background shadow-xl"
          >
            <ProductResults
              id="desktop-product-search-list"
              products={matchingProducts}
              query={query}
              language={language}
              isLoading={isLoading}
              hasError={hasError}
              onRetry={retrySearch}
              onSelect={closeSearch}
              t={t}
            />
          </div>
        )}
      </div>

      <div
        ref={mobileSearchRef}
        className="relative col-span-2 row-start-2 w-full lg:hidden"
      >
        {searchField('mobile-product-search', 'mobile-product-search-results', t('Search products'))}
        {isResultsOpen && hasQuery && (
          <div
            id="mobile-product-search-results"
            className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[min(65dvh,36rem)] overflow-y-auto border border-border bg-background shadow-xl"
          >
            <ProductResults
              id="mobile-product-search-list"
              products={matchingProducts}
              query={query}
              language={language}
              isLoading={isLoading}
              hasError={hasError}
              onRetry={retrySearch}
              onSelect={closeSearch}
              t={t}
            />
          </div>
        )}
      </div>
    </>
  );
}